// Sections-only loading skeleton for the capacities page. Used inside
// `CapacityManagement` when the real page chrome is already mounted but the
// capacities query is still pending (e.g. on date switches). For the
// chunk-load / first-paint case where the entire Page shell is shimmer, see
// CapacitiesPageSkeleton.tsx instead.
//
// Both skeletons share the same section-skeleton shape; keep them in sync
// with app/components/skeletons/capacities.rb.

import { Bar, DEFAULT_LABEL, joinClasses } from './primitives';

const COLUMN_WIDTH_CLASS = 'w-[203px]';
const SERVICE_COLUMN_COUNT = 6;
// `TimeBands::BANDS` is 6 entries; show one trigger-row shimmer per band.
export const CAPACITIES_BAND_ROW_COUNT = 6;
// `TimeSlots.for_date` is 69 entries; rendering all 69 is heavy DOM for a
// sub-second state. Eight rows fills the typical above-the-fold area.
export const CAPACITIES_SLOT_ROW_COUNT = 8;

function ColGroup() {
  return (
    <colgroup>
      <col className={COLUMN_WIDTH_CLASS} />
      {Array.from({ length: SERVICE_COLUMN_COUNT }, (_, i) => (
        <col key={i} className={COLUMN_WIDTH_CLASS} />
      ))}
    </colgroup>
  );
}

function SectionTitleRow({ titleWidthClass }: { titleWidthClass: string }) {
  return (
    <tr>
      <td colSpan={SERVICE_COLUMN_COUNT + 1} className="pt-0 pb-8 bg-off-white">
        <div className="sticky left-0 w-fit">
          <Bar className={joinClasses('h-6', titleWidthClass)} />
        </div>
      </td>
    </tr>
  );
}

function ColumnHeaderRow() {
  return (
    <tr>
      <th
        scope="col"
        className="sticky left-0 z-10 pr-8 pb-2 bg-off-white border-b border-grey-light text-left align-bottom last:pr-0"
      >
        <Bar className="h-4 w-20" />
      </th>
      {Array.from({ length: SERVICE_COLUMN_COUNT }, (_, i) => (
        <th
          key={i}
          scope="col"
          className="pr-8 pb-2 bg-off-white border-b border-grey-light text-left align-bottom last:pr-0"
        >
          <Bar className="h-4 w-24" />
        </th>
      ))}
    </tr>
  );
}

function DefaultsRow({ last }: { last: boolean }) {
  const rowPadding = last ? 'pt-4 pb-4' : 'pt-4';
  const borderClass = last ? 'border-b border-grey-light' : '';
  return (
    <tr>
      <th
        scope="row"
        className={joinClasses(
          'sticky left-0 z-20',
          rowPadding,
          'pr-8 bg-off-white text-left align-middle',
          borderClass,
        )}
      >
        <Bar className="h-3 w-14" />
      </th>
      {Array.from({ length: SERVICE_COLUMN_COUNT }, (_, i) => (
        <td
          key={i}
          className={joinClasses(rowPadding, 'pr-8 align-middle bg-off-white', borderClass)}
        >
          <Bar className="h-11 w-full rounded-xs" />
        </td>
      ))}
    </tr>
  );
}

function TriggerRow() {
  return (
    <tr>
      <th
        scope="row"
        className="sticky left-0 z-20 pt-8 pb-2 pr-8 bg-off-white text-left align-middle last:pr-0 border-b border-grey-light"
      >
        <Bar className="h-5 w-32" />
      </th>
      {Array.from({ length: SERVICE_COLUMN_COUNT }, (_, i) => (
        <td key={i} aria-hidden="true" className="pt-8 pb-2 border-b border-grey-light" />
      ))}
    </tr>
  );
}

function SectionHeader({
  titleWidthClass,
  stickyTopPx,
}: {
  titleWidthClass: string;
  stickyTopPx: number;
}) {
  return (
    <div className="sticky z-30 w-full min-w-0 bg-off-white" style={{ top: `${stickyTopPx}px` }}>
      <div className="capacities-scroll-x-hidden min-w-0 overflow-x-auto overflow-y-hidden">
        <div className="w-max">
          <table className="table-fixed w-max border-separate border-spacing-0">
            <ColGroup />
            <thead className="bg-off-white">
              <SectionTitleRow titleWidthClass={titleWidthClass} />
              <ColumnHeaderRow />
              <DefaultsRow last={false} />
              <DefaultsRow last={true} />
            </thead>
          </table>
        </div>
      </div>
    </div>
  );
}

function SectionBody({ rowCount }: { rowCount: number }) {
  return (
    <div className="capacities-scroll-x min-w-0 overflow-x-auto overflow-y-hidden">
      <table className="table-fixed w-max border-separate border-spacing-0">
        <ColGroup />
        <tbody>
          {Array.from({ length: rowCount }, (_, i) => (
            <TriggerRow key={i} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CapacitiesSectionSkeleton({
  titleWidthClass,
  rowCount,
  stickyTopPx,
}: {
  titleWidthClass: string;
  rowCount: number;
  stickyTopPx: number;
}) {
  return (
    <section className="flex flex-col bg-off-white">
      <SectionHeader titleWidthClass={titleWidthClass} stickyTopPx={stickyTopPx} />
      <SectionBody rowCount={rowCount} />
    </section>
  );
}

export interface CapacitiesSkeletonProps {
  /** Distance from the top of the vertical scrollport — typically the toolbar height. */
  stickyTopPx: number;
  /** Screen-reader label on the outer `role="status"` wrapper. */
  label?: string;
}

export function CapacitiesSkeleton({ stickyTopPx, label = DEFAULT_LABEL }: CapacitiesSkeletonProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className="flex min-w-0 flex-col [&>*+*]:mt-4"
    >
      <CapacitiesSectionSkeleton
        titleWidthClass="w-32"
        rowCount={CAPACITIES_BAND_ROW_COUNT}
        stickyTopPx={stickyTopPx}
      />
      <CapacitiesSectionSkeleton
        titleWidthClass="w-28"
        rowCount={CAPACITIES_SLOT_ROW_COUNT}
        stickyTopPx={stickyTopPx}
      />
    </div>
  );
}
